import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { useTheme, adminTheme } from '@/context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Briefcase,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  MapPin,
  Clock,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function AllJobs() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      await axios.put(`${API}/jobs/${jobId}`, { status: newStatus });
      toast.success(`Job status updated to ${newStatus}`);
      fetchJobs();
    } catch (error) {
      toast.error('Failed to update job status');
    }
  };

  const handleDelete = async (jobId) => {
    if (!confirm('Are you sure you want to delete this job?')) return;
    
    try {
      await axios.delete(`${API}/jobs/${jobId}`);
      toast.success('Job deleted successfully');
      fetchJobs();
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          job.location?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Active', className: 'bg-green-100 text-green-700', icon: CheckCircle },
      filled: { label: 'Filled', className: 'bg-blue-100 text-blue-700', icon: Users },
      removed: { label: 'Removed', className: 'bg-red-100 text-red-700', icon: XCircle },
      draft: { label: 'Draft', className: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
    };
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    return (
      <Badge className={`${config.className} gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Stats
  const stats = {
    total: jobs.length,
    active: jobs.filter(j => j.status === 'active').length,
    filled: jobs.filter(j => j.status === 'filled').length,
    removed: jobs.filter(j => j.status === 'removed').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${adminTheme.textPrimary(isDark)}`}>All Jobs</h1>
          <p className={adminTheme.textSecondary(isDark)}>Manage all job postings</p>
        </div>
        <Link to="/admin/add-job">
          <Button className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Add New Job
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`border ${adminTheme.cardBg(isDark)}`}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
              <Briefcase className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className={`text-2xl font-bold ${adminTheme.textPrimary(isDark)}`}>{stats.total}</p>
              <p className={`text-sm ${adminTheme.textSecondary(isDark)}`}>Total Jobs</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`border ${adminTheme.cardBg(isDark)}`}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
              <CheckCircle className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className={`text-2xl font-bold ${adminTheme.textPrimary(isDark)}`}>{stats.active}</p>
              <p className={`text-sm ${adminTheme.textSecondary(isDark)}`}>Active</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`border ${adminTheme.cardBg(isDark)}`}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
              <Users className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className={`text-2xl font-bold ${adminTheme.textPrimary(isDark)}`}>{stats.filled}</p>
              <p className={`text-sm ${adminTheme.textSecondary(isDark)}`}>Filled</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`border ${adminTheme.cardBg(isDark)}`}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-red-500/20' : 'bg-red-100'}`}>
              <XCircle className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <p className={`text-2xl font-bold ${adminTheme.textPrimary(isDark)}`}>{stats.removed}</p>
              <p className={`text-sm ${adminTheme.textSecondary(isDark)}`}>Removed</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className={`border ${adminTheme.cardBg(isDark)}`}>
        <CardHeader className="pb-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle className={adminTheme.textPrimary(isDark)}>Job Listings</CardTitle>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${adminTheme.textMuted(isDark)}`} />
                <Input
                  placeholder="Search jobs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-10 w-full sm:w-64 ${adminTheme.inputBg(isDark)}`}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className={`w-full sm:w-40 ${adminTheme.inputBg(isDark)}`}>
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className={adminTheme.cardBg(isDark)}>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="filled">Filled</SelectItem>
                  <SelectItem value="removed">Removed</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className={`w-12 h-12 mx-auto mb-4 ${adminTheme.textMuted(isDark)}`} />
              <h3 className={`text-lg font-semibold mb-2 ${adminTheme.textPrimary(isDark)}`}>No jobs found</h3>
              <p className={`mb-4 ${adminTheme.textSecondary(isDark)}`}>Start by creating your first job posting</p>
              <Link to="/admin/add-job">
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Job
                </Button>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className={adminTheme.border(isDark)}>
                    <TableHead className={adminTheme.textSecondary(isDark)}>Job Title</TableHead>
                    <TableHead className={adminTheme.textSecondary(isDark)}>Location</TableHead>
                    <TableHead className={adminTheme.textSecondary(isDark)}>Type</TableHead>
                    <TableHead className={adminTheme.textSecondary(isDark)}>Salary</TableHead>
                    <TableHead className={adminTheme.textSecondary(isDark)}>Status</TableHead>
                    <TableHead className={adminTheme.textSecondary(isDark)}>Posted</TableHead>
                    <TableHead className={`text-right ${adminTheme.textSecondary(isDark)}`}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredJobs.map((job) => (
                    <TableRow key={job.id} className={`${adminTheme.tableRowHover(isDark)} ${adminTheme.border(isDark)}`}>
                      <TableCell>
                        <div>
                          <p className={`font-medium ${adminTheme.textPrimary(isDark)}`}>{job.title}</p>
                          <p className={`text-sm ${adminTheme.textSecondary(isDark)}`}>{job.company || 'Aurous Consultancy'}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={`flex items-center gap-1 ${adminTheme.textSecondary(isDark)}`}>
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={isDark ? 'border-slate-600 text-slate-300' : ''}>{job.job_type || 'Full-time'}</Badge>
                      </TableCell>
                      <TableCell className={adminTheme.textSecondary(isDark)}>{job.salary_range || 'Not specified'}</TableCell>
                      <TableCell>{getStatusBadge(job.status)}</TableCell>
                      <TableCell>
                        <div className={`flex items-center gap-1 text-sm ${adminTheme.textMuted(isDark)}`}>
                          <Clock className="w-4 h-4" />
                          {formatDate(job.created_at)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className={isDark ? 'hover:bg-slate-700' : ''}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/admin/edit-job/${job.id}`)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => window.open(`/careers`, '_blank')}>
                              <Eye className="w-4 h-4 mr-2" />
                              View on Website
                            </DropdownMenuItem>
                            {job.status === 'active' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(job.id, 'filled')}>
                                <Users className="w-4 h-4 mr-2" />
                                Mark as Filled
                              </DropdownMenuItem>
                            )}
                            {job.status !== 'active' && job.status !== 'removed' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(job.id, 'active')}>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Set Active
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => handleDelete(job.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
